import * as React from "react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger } from "./select";

type OptionProps = React.ComponentProps<"option">;
interface Option { value: string; label: React.ReactNode; disabled?: boolean; group?: string; style?: React.CSSProperties }
const EMPTY = "__ando_empty_selection__";

function optionsFrom(children: React.ReactNode, group?: string): Option[] {
  return React.Children.toArray(children).flatMap((child): Option[] => {
    if (!React.isValidElement(child)) return [];
    if (child.type === React.Fragment) return optionsFrom((child.props as { children?: React.ReactNode }).children, group);
    if (child.type === "optgroup") {
      const props = child.props as React.ComponentProps<"optgroup">;
      return optionsFrom(props.children, props.label).map((option) => ({ ...option, disabled: props.disabled || option.disabled }));
    }
    if (child.type !== "option") return [];
    const props = child.props as OptionProps;
    return [{ value: String(props.value ?? props.children ?? ""), label: props.children, disabled: props.disabled, group, style: props.style }];
  });
}

/** Native-shaped single-choice API, rendered exclusively with Ando's shared Select. */
export const StyledSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(function StyledSelect({ children, value, defaultValue, onChange, disabled, required, name, className, id, title, style, onBlur, onFocus, multiple: _multiple, size: _size, ...props }, forwardedRef) {
  const options = optionsFrom(children);
  const [localValue, setLocalValue] = React.useState(() => String(defaultValue ?? options[0]?.value ?? ""));
  const current = value === undefined ? localValue : String(value);
  const encode = (v: string) => v === "" ? EMPTY : v;
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  React.useImperativeHandle(forwardedRef, () => triggerRef.current as unknown as HTMLSelectElement);
  const notify = (next: string) => {
    const decoded = next === EMPTY ? "" : next;
    // Existing handlers consume target/currentTarget.value, name and id. Keep
    // that contract without leaking the Radix empty-value sentinel.
    const target = { value: decoded, name: name ?? "", id: id ?? "" } as HTMLSelectElement;
    onChange?.({ target, currentTarget: target, type: "change", preventDefault() {}, stopPropagation() {} } as React.ChangeEvent<HTMLSelectElement>);
    setLocalValue(target.value);
  };
  const grouped: Array<{ label?: string; options: Option[] }> = [];
  for (const option of options) {
    const previous = grouped[grouped.length - 1];
    if (previous && previous.label === option.group) previous.options.push(option);
    else grouped.push({ label: option.group, options: [option] });
  }
  return (
    <><input type="hidden" name={name} value={current} disabled={disabled} form={props.form} />
    <Select value={encode(current)} onValueChange={notify} disabled={disabled} required={required}>
      <SelectTrigger {...props as unknown as React.ButtonHTMLAttributes<HTMLButtonElement>} ref={triggerRef} id={id} name={name} value={current} title={title} style={style} className={className}
        aria-label={props["aria-label"]} aria-labelledby={props["aria-labelledby"]}
        aria-describedby={props["aria-describedby"]} aria-invalid={props["aria-invalid"]}
        tabIndex={props.tabIndex} autoFocus={props.autoFocus}
        onBlur={onBlur as unknown as React.FocusEventHandler<HTMLButtonElement>}
        onFocus={onFocus as unknown as React.FocusEventHandler<HTMLButtonElement>}
        data-testid={(props as Record<string, unknown>)["data-testid"] as string | undefined}
      />
      <SelectContent>
        {grouped.map((group, index) => {
          const items = group.options.map((option) => <SelectItem key={option.value} value={encode(option.value)} disabled={option.disabled} style={option.style}>{option.label}</SelectItem>);
          return group.label ? <SelectGroup key={`${group.label}-${index}`}><SelectLabel>{group.label}</SelectLabel>{items}</SelectGroup> : <React.Fragment key={index}>{items}</React.Fragment>;
        })}
      </SelectContent>
    </Select></>
  );
});
